/* global React */
const { useEffect, useRef } = React;

// ─── OrbAle ───────────────────────────────────────────────────────────────
// Animated glassy orb that follows the cursor. Self-contained: sizes to its
// container, listens to window pointer events, and runs its own rAF loop.
// Source physics ported from the orb.html design deliverable.

function OrbAle({ className = "", ariaLabel = "ELIA — personaje agéntico" }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");

    let DPR = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    let R = 120;
    const PAD = 1.18;
    let SIZE = 0;
    let cx = 0, cy = 0;

    function resize() {
      DPR = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
      const rect = wrap.getBoundingClientRect();
      const side = Math.max(40, Math.min(rect.width, rect.height));
      R = (side / 2) / PAD;
      SIZE = Math.ceil(R * 2 * PAD);
      cx = SIZE / 2;
      cy = SIZE / 2;
      canvas.style.width = SIZE + "px";
      canvas.style.height = SIZE + "px";
      canvas.width = Math.round(SIZE * DPR);
      canvas.height = Math.round(SIZE * DPR);
    }
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(wrap);
    window.addEventListener("resize", resize);

    // pointer in viewport coords
    let pointer = { x: window.innerWidth / 2, y: window.innerHeight * 0.35 };
    let hasPointer = false;

    function setPointer(clientX, clientY) {
      pointer.x = clientX;
      pointer.y = clientY;
      hasPointer = true;
    }
    function onLeave() { hasPointer = false; }
    const onMove = (e) => setPointer(e.clientX, e.clientY);
    const onDown = (e) => setPointer(e.clientX, e.clientY);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerleave", onLeave);

    // spring state
    const face = { x: 0, y: 0, vx: 0, vy: 0 };
    let squishX = 1, squishY = 1, squishAng = 0;
    let blink = 0;
    let blinkTimer = 1.4 + Math.random() * 3;
    let blinking = false;
    let blinkPhase = 0;
    let t = 0;

    // dither noise
    const noise = document.createElement("canvas");
    noise.width = 128; noise.height = 128;
    {
      const ng = noise.getContext("2d");
      const img = ng.createImageData(128, 128);
      for (let i = 0; i < img.data.length; i += 4) {
        const v = (Math.random() * 255) | 0;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
        img.data[i + 3] = 255;
      }
      ng.putImageData(img, 0, 0);
    }

    const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
    const lerp = (a, b, k) => a + (b - a) * k;

    function capsule(g, x, y, w, h, rot) {
      const r = Math.min(w, h) / 2;
      g.save();
      g.translate(x, y);
      g.rotate(rot);
      g.beginPath();
      const hw = w / 2, hh = h / 2;
      g.moveTo(-hw, -hh);
      g.lineTo(hw, -hh);
      g.arcTo(hw, -hh, hw, -hh + r, r);
      g.lineTo(hw, hh - r);
      g.arcTo(hw, hh, hw - r, hh, r);
      g.lineTo(-hw + r, hh);
      g.arcTo(-hw, hh, -hw, hh - r, r);
      g.lineTo(-hw, -hh + r);
      g.arcTo(-hw, -hh, -hw + r, -hh, r);
      g.closePath();
      g.restore();
    }

    function drawBody(g) {
      g.save();
      g.beginPath();
      g.arc(0, 0, R, 0, Math.PI * 2);
      g.clip();

      g.fillStyle = "#060410";
      g.fillRect(-R, -R, R * 2, R * 2);

      const ballX = -R * 0.12, ballY = -R * 0.02;
      const ball = g.createRadialGradient(ballX, ballY, R * 0.02, ballX, ballY, R * 1.04);
      ball.addColorStop(0.0, "#c98bd9");
      ball.addColorStop(0.16, "#BE76CF");
      ball.addColorStop(0.52, "#461E88");
      ball.addColorStop(0.82, "#1c0e3a");
      ball.addColorStop(1.0, "#050310");
      g.globalCompositeOperation = "source-over";
      g.fillStyle = ball;
      g.fillRect(-R, -R, R * 2, R * 2);

      const water = g.createRadialGradient(R * 0.62, -R * 0.16, R * 0.04, R * 0.42, -R * 0.06, R * 1.25);
      water.addColorStop(0.0, "rgba(150,205,228,0.95)");
      water.addColorStop(0.22, "rgba(86,160,200,0.88)");
      water.addColorStop(0.46, "rgba(40,104,158,0.70)");
      water.addColorStop(0.70, "rgba(22,62,110,0.34)");
      water.addColorStop(1.0, "rgba(16,44,84,0.0)");
      g.fillStyle = water;
      g.fillRect(-R, -R, R * 2, R * 2);

      const teal = g.createRadialGradient(-R * 0.95, -R * 0.1, R * 0.02, -R * 0.55, R * 0.05, R * 0.95);
      teal.addColorStop(0.0, "rgba(150,222,224,0.85)");
      teal.addColorStop(0.30, "rgba(86,184,196,0.45)");
      teal.addColorStop(0.62, "rgba(50,130,150,0.12)");
      teal.addColorStop(1.0, "rgba(40,120,140,0.0)");
      g.globalCompositeOperation = "screen";
      g.fillStyle = teal;
      g.fillRect(-R, -R, R * 2, R * 2);

      g.globalCompositeOperation = "source-over";
      const vg = g.createRadialGradient(0, 0, R * 0.66, 0, 0, R * 1.02);
      vg.addColorStop(0.0, "rgba(8,14,28,0)");
      vg.addColorStop(0.86, "rgba(7,12,24,0)");
      vg.addColorStop(0.96, "rgba(9,14,26,0.18)");
      vg.addColorStop(1.0, "rgba(8,12,22,0.45)");
      g.fillStyle = vg;
      g.fillRect(-R, -R, R * 2, R * 2);

      g.restore();
    }

    function drawFace(g) {
      g.save();
      g.beginPath();
      g.arc(0, 0, R, 0, Math.PI * 2);
      g.clip();

      const off = Math.hypot(face.x, face.y);
      const offN = clamp(off / (R * 0.6), 0, 1);
      const fore = 1 - 0.32 * offN;

      const eyeH = R * 0.40;
      const eyeW = R * 0.155;
      const gap = R * 0.07;
      const tilt = clamp(face.x / R, -1, 1) * 0.07;

      const flx = face.x + eyeW * 1.9;
      const fly = face.y - eyeH * 0.18;
      const fl = g.createRadialGradient(flx, fly, 0, flx, fly, R * 0.16);
      fl.addColorStop(0.0, "rgba(255,225,250,0.55)");
      fl.addColorStop(0.4, "rgba(232,150,220,0.22)");
      fl.addColorStop(1.0, "rgba(232,150,220,0.0)");
      g.globalCompositeOperation = "screen";
      g.fillStyle = fl;
      g.beginPath();
      g.arc(flx, fly, R * 0.16, 0, Math.PI * 2);
      g.fill();

      g.fillStyle = "rgba(255,255,255,0.85)";
      g.beginPath();
      g.arc(flx + eyeW * 0.5, fly - eyeH * 0.18, R * 0.012, 0, Math.PI * 2);
      g.fill();

      g.globalCompositeOperation = "source-over";
      const blinkScale = 1 - blink * 0.92;

      function eye(ex, scale) {
        const x = face.x + ex;
        const y = face.y;
        g.save();
        g.shadowColor = "rgba(120,180,220,0.5)";
        g.shadowBlur = R * 0.05;
        const w = eyeW * fore * scale;
        const h = eyeH * blinkScale;
        const eg = g.createLinearGradient(x - w, y - h, x + w, y + h);
        eg.addColorStop(0, "rgba(245,247,250,0.96)");
        eg.addColorStop(1, "rgba(214,224,236,0.96)");
        g.fillStyle = eg;
        capsule(g, x, y, w, h, tilt);
        g.fill();
        g.restore();
      }

      eye(-(eyeW / 2 + gap / 2), 1.0);
      eye(+(eyeW / 2 + gap / 2), 0.94);

      g.restore();
    }

    function drawGloss(g) {
      g.save();
      g.beginPath();
      g.arc(0, 0, R, 0, Math.PI * 2);
      g.clip();

      g.save();
      g.globalCompositeOperation = "overlay";
      g.globalAlpha = 0.05;
      const pat = g.createPattern(noise, "repeat");
      g.fillStyle = pat;
      g.fillRect(-R, -R, R * 2, R * 2);
      g.restore();

      g.globalCompositeOperation = "screen";

      g.save();
      g.globalCompositeOperation = "screen";
      const lrim = g.createRadialGradient(-R * 1.02, -R * 0.06, R * 0.04, -R * 0.78, -R * 0.02, R * 0.62);
      lrim.addColorStop(0.0, "rgba(168,235,236,0.95)");
      lrim.addColorStop(0.32, "rgba(110,200,206,0.55)");
      lrim.addColorStop(0.66, "rgba(64,150,168,0.16)");
      lrim.addColorStop(1.0, "rgba(64,150,168,0.0)");
      g.fillStyle = lrim;
      g.fillRect(-R, -R, R * 2, R * 2);
      g.restore();

      g.save();
      g.globalCompositeOperation = "screen";
      const topSheen = g.createRadialGradient(R * 0.05, -R * 0.78, R * 0.05, R * 0.05, -R * 0.5, R * 0.85);
      topSheen.addColorStop(0.0, "rgba(180,210,235,0.42)");
      topSheen.addColorStop(0.5, "rgba(120,160,205,0.16)");
      topSheen.addColorStop(1.0, "rgba(120,160,205,0.0)");
      g.fillStyle = topSheen;
      g.fillRect(-R, -R, R * 2, R * 2);
      g.restore();

      g.save();
      g.shadowColor = "rgba(255,255,255,0.85)";
      g.shadowBlur = R * 0.06;
      const sp = g.createRadialGradient(-R * 0.30, -R * 0.34, 0, -R * 0.30, -R * 0.34, R * 0.20);
      sp.addColorStop(0.0, "rgba(255,255,255,0.95)");
      sp.addColorStop(0.45, "rgba(255,255,255,0.55)");
      sp.addColorStop(1.0, "rgba(255,255,255,0.0)");
      g.fillStyle = sp;
      g.beginPath();
      g.arc(-R * 0.30, -R * 0.34, R * 0.20, 0, Math.PI * 2);
      g.fill();
      g.restore();

      g.save();
      const sp2 = g.createRadialGradient(R * 0.06, -R * 0.40, 0, R * 0.06, -R * 0.40, R * 0.11);
      sp2.addColorStop(0.0, "rgba(255,255,255,0.85)");
      sp2.addColorStop(0.5, "rgba(255,255,255,0.4)");
      sp2.addColorStop(1.0, "rgba(255,255,255,0.0)");
      g.fillStyle = sp2;
      g.beginPath();
      g.arc(R * 0.06, -R * 0.40, R * 0.11, 0, Math.PI * 2);
      g.fill();
      g.restore();

      g.save();
      const lb = g.createRadialGradient(R * 0.1, R * 0.62, 0, R * 0.1, R * 0.62, R * 0.5);
      lb.addColorStop(0.0, "rgba(120,150,210,0.25)");
      lb.addColorStop(1.0, "rgba(120,150,210,0.0)");
      g.fillStyle = lb;
      g.beginPath();
      g.arc(R * 0.1, R * 0.62, R * 0.5, 0, Math.PI * 2);
      g.fill();
      g.restore();

      g.restore();

      g.save();
      g.globalCompositeOperation = "source-over";
      g.lineWidth = Math.max(1, R * 0.012);
      const edge = g.createLinearGradient(-R, -R, R, R);
      edge.addColorStop(0, "rgba(180,240,245,0.45)");
      edge.addColorStop(0.5, "rgba(90,130,170,0.15)");
      edge.addColorStop(1, "rgba(40,30,70,0.5)");
      g.strokeStyle = edge;
      g.beginPath();
      g.arc(0, 0, R - g.lineWidth * 0.5, 0, Math.PI * 2);
      g.stroke();
      g.restore();
    }

    let last = performance.now();
    let raf = 0;
    let alive = true;

    function frame(now) {
      if (!alive) return;
      let dt = (now - last) / 1000;
      last = now;
      if (dt > 0.05) dt = 0.05;
      t += dt;

      const rect = canvas.getBoundingClientRect();
      const ocx = rect.left + rect.width / 2;
      const ocy = rect.top + rect.height / 2;

      let tx = 0, ty = 0;
      if (hasPointer) {
        const dx = pointer.x - ocx;
        const dy = pointer.y - ocy;
        const d = Math.hypot(dx, dy) || 1;
        const reach = clamp(d / (R * 1.6), 0, 1);
        const maxOff = R * 0.46;
        tx = (dx / d) * reach * maxOff;
        ty = (dy / d) * reach * maxOff;
      } else {
        tx = Math.cos(t * 0.5) * R * 0.08;
        ty = Math.sin(t * 0.7) * R * 0.06;
      }

      const k = 120;
      const damp = 14;
      const ax = (tx - face.x) * k - face.vx * damp;
      const ay = (ty - face.y) * k - face.vy * damp;
      face.vx += ax * dt;
      face.vy += ay * dt;
      face.x += face.vx * dt;
      face.y += face.vy * dt;

      const speed = Math.hypot(face.vx, face.vy);
      const sAmt = clamp(speed / (R * 9), 0, 1) * 0.12;
      squishAng = Math.atan2(face.vy, face.vx);
      const tgX = 1 + sAmt;
      const tgY = 1 - sAmt;
      squishX = lerp(squishX, tgX, 0.2);
      squishY = lerp(squishY, tgY, 0.2);

      if (!blinking) {
        blinkTimer -= dt;
        if (blinkTimer <= 0) { blinking = true; blinkPhase = 0; }
      } else {
        blinkPhase += dt / 0.16;
        blink = blinkPhase < 0.5
          ? (blinkPhase / 0.5)
          : 1 - (blinkPhase - 0.5) / 0.5;
        blink = clamp(blink, 0, 1);
        if (blinkPhase >= 1) {
          blinking = false; blink = 0;
          blinkTimer = 2.2 + Math.random() * 3.5;
        }
      }

      const breathe = 1 + Math.sin(t * 1.6) * 0.012;
      render(breathe);
      raf = requestAnimationFrame(frame);
    }

    function render(breathe) {
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      ctx.clearRect(0, 0, SIZE, SIZE);

      ctx.save();
      ctx.translate(cx, cy);

      ctx.save();
      const glow = ctx.createRadialGradient(0, 0, R * 0.98, 0, 0, R * 1.14);
      glow.addColorStop(0, "rgba(80,150,190,0.10)");
      glow.addColorStop(1, "rgba(80,150,190,0.0)");
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(0, 0, R * 1.14, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.rotate(squishAng);
      ctx.scale(squishX * breathe, squishY * breathe);
      ctx.rotate(-squishAng);

      drawBody(ctx);
      drawFace(ctx);
      drawGloss(ctx);

      ctx.restore();
    }

    raf = requestAnimationFrame(frame);

    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className={`orb-ale ${className}`}
      role="img"
      aria-label={ariaLabel}
    >
      <canvas ref={canvasRef} className="orb-ale-canvas" />
    </div>
  );
}

window.OrbAle = OrbAle;
